/**
 * Parse mixed corporate hero queries: person + brand + city + domain.
 *
 * Aligned with backend ``utils.hybrid_entity.segment_hybrid_entity``:
 * brand window can appear anywhere; facility tokens (Hospital, SRL, …) stay with company.
 *
 * Examples:
 * - ``SmartChoice smartchoice.ro iasi lucian mihalache``
 * - ``arcadiamedical.ro iasi Arcadia Hospital``
 * - ``raluca fiterman arcadiamedical.ro iasi Arcadia Hospital``
 */
import {
  findDomainInQuery,
  isValidDomainHost,
} from './homeSearchUtils';

const RO_LOCATION_ALIASES = new Map([
  ['iasi', 'Iasi'],
  ['iași', 'Iasi'],
  ['bucuresti', 'Bucuresti'],
  ['bucurești', 'Bucuresti'],
  ['bucharest', 'Bucuresti'],
  ['cluj', 'Cluj-Napoca'],
  ['cluj-napoca', 'Cluj-Napoca'],
  ['timisoara', 'Timisoara'],
  ['timișoara', 'Timisoara'],
  ['brasov', 'Brasov'],
  ['brașov', 'Brasov'],
  ['constanta', 'Constanta'],
  ['constanța', 'Constanta'],
  ['craiova', 'Craiova'],
  ['oradea', 'Oradea'],
  ['sibiu', 'Sibiu'],
  ['ploiesti', 'Ploiesti'],
  ['ploiești', 'Ploiesti'],
  ['galati', 'Galati'],
  ['galați', 'Galati'],
  ['arad', 'Arad'],
  ['bacau', 'Bacau'],
  ['bacău', 'Bacau'],
  ['suceava', 'Suceava'],
  ['pitesti', 'Pitesti'],
  ['pitești', 'Pitesti'],
]);

/** Facility / legal descriptors — belong to company, never person focus. */
const NON_PERSON_ENTITY_TOKENS = new Set([
  'hospital',
  'spital',
  'clinic',
  'clinica',
  'medical',
  'medicale',
  'centru',
  'center',
  'centre',
  'group',
  'holding',
  'company',
  'corp',
  'srl',
  'sa',
  'llc',
  'ltd',
  'inc',
  'innovation',
]);

function cleanToken(token) {
  return String(token || '')
    .replace(/^[(["']+/, '')
    .replace(/[.,;:!?)["']+$/, '')
    .trim();
}

function titleCaseLocation(value) {
  const key = String(value || '').trim().toLowerCase();
  return RO_LOCATION_ALIASES.get(key) || key.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function titleCasePhrase(phrase) {
  return String(phrase || '')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
    .filter(Boolean)
    .join(' ');
}

function foldAlnum(token) {
  return String(token || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function compactJoin(tokens) {
  return (tokens || []).map(foldAlnum).join('');
}

/**
 * Contiguous token window whose compact form matches the domain root
 * (``smart choice`` ≡ ``smartchoice`` ≡ ``SmartChoice``).
 *
 * @param {string[]} nameTokens
 * @param {string} domain
 * @returns {[number, number]|null} [start, end)
 */
function findBrandTokenWindow(nameTokens, domain) {
  const host = String(domain || '')
    .trim()
    .toLowerCase()
    .replace(/^www\./, '');
  if (!host || !nameTokens?.length) return null;
  const root = foldAlnum(host.split('.')[0] || '');
  if (root.length < 4) return null;
  const n = nameTokens.length;
  for (let size = Math.min(4, n); size >= 1; size -= 1) {
    for (let i = 0; i <= n - size; i += 1) {
      const compound = compactJoin(nameTokens.slice(i, i + size));
      if (!compound) continue;
      if (compound === root || (compound.length >= 6 && root.includes(compound))) {
        return [i, i + size];
      }
    }
  }
  return null;
}

/**
 * Infer scan country from corporate domain TLD.
 *
 * @param {string} domain
 * @returns {'RO' | 'GLOBAL'}
 */
export function inferCompanyCountryFromDomain(domain) {
  const host = String(domain || '').trim().toLowerCase().replace(/^www\./, '');
  if (host.endsWith('.ro')) return 'RO';
  return 'GLOBAL';
}

/**
 * @param {string} raw
 * @returns {{
 *   domain: string,
 *   companyName: string,
 *   personFocus: string,
 *   streamName: string,
 *   locationTags: string[],
 *   country: 'RO' | 'GLOBAL',
 * }}
 */
export function parseCorporateHeroQuery(raw) {
  const text = String(raw || '').trim();
  const domain = findDomainInQuery(text) || '';
  const tokens = text.split(/\s+/).map(cleanToken).filter(Boolean);
  const locationTags = [];
  const nameTokens = [];

  for (const token of tokens) {
    const lower = token.toLowerCase();
    const host = token.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split(/[/?#]/)[0];
    if (domain && (lower === domain.toLowerCase() || host.toLowerCase() === domain.toLowerCase())) {
      continue;
    }
    if (isValidDomainHost(host)) continue;
    if (RO_LOCATION_ALIASES.has(lower)) {
      const loc = titleCaseLocation(lower);
      if (!locationTags.some((x) => x.toLowerCase() === loc.toLowerCase())) {
        locationTags.push(loc);
      }
      continue;
    }
    nameTokens.push(token);
  }

  let companyTokens = [];
  let personTokens = [];

  const window = findBrandTokenWindow(nameTokens, domain);
  if (window) {
    let [start, end] = window;
    // Absorb facility / legal descriptors into the company window (Arcadia Hospital).
    while (end < nameTokens.length) {
      const nxt = foldAlnum(nameTokens[end]);
      if (NON_PERSON_ENTITY_TOKENS.has(nxt)) {
        end += 1;
        continue;
      }
      break;
    }
    companyTokens = nameTokens.slice(start, end);
    personTokens = [...nameTokens.slice(0, start), ...nameTokens.slice(end)];
  } else if (nameTokens.length >= 3) {
    personTokens = nameTokens.slice(0, 2);
    companyTokens = nameTokens.slice(2);
  } else if (nameTokens.length) {
    companyTokens = [...nameTokens];
  }

  // Peel leading non-person crumbs from person into company.
  while (personTokens.length) {
    const lead = foldAlnum(personTokens[0]);
    if (NON_PERSON_ENTITY_TOKENS.has(lead)) {
      companyTokens.push(personTokens.shift());
      continue;
    }
    break;
  }

  let companyName = companyTokens.join(' ').trim();
  let personFocus = personTokens.join(' ').trim();

  if (!companyName && domain) {
    companyName = domain;
  }

  companyName = titleCasePhrase(companyName) || companyName;
  personFocus = titleCasePhrase(personFocus) || personFocus;

  if (personFocus && personFocus.toLowerCase() === companyName.toLowerCase()) {
    personFocus = '';
  }

  const streamName =
    personFocus && companyName ? `${companyName} | ${personFocus}` : companyName;
  const country = inferCompanyCountryFromDomain(domain);

  return {
    domain,
    companyName,
    personFocus,
    streamName,
    locationTags,
    country,
  };
}
